import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, Eye, EyeOff, MonitorSmartphone, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

export default function AuthModal({ open, onClose, initialTab = "register" }) {
  const { setSession } = useAuth();
  const { t, lang } = useI18n();
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [deviceLimit, setDeviceLimit] = useState(null); // { sessions, email, password } | null
  const [revoking, setRevoking] = useState(null); // sid being revoked
  React.useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "", company_name: "",
    situation: "", goals: "", interests: "", tone: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const startCheckout = async () => {
    const { data } = await api.post("/payments/checkout", {
      type: "lifetime",
      origin_url: window.location.origin,
    });
    window.location.href = data.url;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        company_name: form.company_name,
        lang,
        profile: {
          situation: form.situation, goals: form.goals,
          interests: form.interests, tone: form.tone,
        },
      });
      setSession(data.token, data.user);
      toast.success("Compte créé ! Redirection vers le paiement sécurisé…");
      await startCheckout();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const doLogin = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession(data.token, data.user);
    if (data.user.is_admin) { window.location.href = "/admin"; return; }
    if (data.user.is_paid) { window.location.href = "/dashboard"; return; }
    toast.info("Bienvenue ! Activez votre accès à vie pour continuer.");
    await startCheckout();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await doLogin(form.email, form.password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "too_many_devices") {
        setDeviceLimit({ sessions: detail.sessions || [], email: form.email, password: form.password });
        setLoading(false);
        return;
      }
      toast.error(formatApiError(detail));
      setLoading(false);
    }
  };

  const disconnectAndRetry = async (sid) => {
    setRevoking(sid);
    try {
      await api.post("/auth/sessions/revoke", { email: deviceLimit.email, password: deviceLimit.password, sid });
      const email = deviceLimit.email, password = deviceLimit.password;
      setDeviceLimit(null);
      setLoading(true);
      await doLogin(email, password);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading(false);
    } finally {
      setRevoking(null);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email, lang });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 rounded-[12px] h-11 focus-visible:ring-[#007BFF]";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && (setDeviceLimit(null), onClose())}>
      <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md p-0 overflow-hidden">
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
              <span className="text-[#007BFF] text-glow-blue">GaimTime</span>
            </DialogTitle>
          </DialogHeader>
          {!deviceLimit && (
          <div className="flex gap-2 mt-4 bg-[#0A0A0A] p-1 rounded-[12px]">
            <button data-testid="auth-tab-register" onClick={() => setTab("register")}
              className={`flex-1 py-2 rounded-[10px] text-sm font-semibold transition-all ${tab === "register" ? "bg-[#007BFF] text-white" : "text-white/50"}`}
            >{t("create_acc")}</button>
            <button data-testid="auth-tab-login" onClick={() => setTab("login")}
              className={`flex-1 py-2 rounded-[10px] text-sm font-semibold transition-all ${tab === "login" ? "bg-[#007BFF] text-white" : "text-white/50"}`}
            >{t("sign_in")}</button>
          </div>
          )}
        </div>

        <div className="px-6 pb-6 pt-4 max-h-[70vh] overflow-y-auto">
          {deviceLimit ? (
            <div className="space-y-4" data-testid="device-limit-panel">
              <div className="flex items-start gap-3 rounded-[12px] border border-[#FFB020]/40 bg-[#FFB020]/10 p-3">
                <AlertTriangle size={20} className="text-[#FFB020] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">{t("dev_limit_title")}</div>
                  <div className="text-white/60 text-xs mt-1 leading-relaxed">{t("dev_limit_desc")}</div>
                </div>
              </div>
              <div className="space-y-2">
                {deviceLimit.sessions.map((s) => (
                  <div key={s.sid} data-testid="device-row" className="flex items-center gap-3 rounded-[12px] border border-white/10 bg-[#0A0A0A] px-3 py-3">
                    <MonitorSmartphone size={18} className="text-[#007BFF] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.device}</div>
                      {s.last_seen && <div className="text-white/40 text-xs">{t("dev_seen")}: {new Date(s.last_seen).toLocaleString()}</div>}
                    </div>
                    <Button data-testid="device-disconnect-btn" onClick={() => disconnectAndRetry(s.sid)} disabled={revoking !== null}
                      className="bg-[#E5484D] hover:bg-[#c93b40] rounded-[10px] h-9 px-3 text-sm shrink-0">
                      {revoking === s.sid ? <Loader2 className="animate-spin" size={16} /> : t("dev_disconnect")}
                    </Button>
                  </div>
                ))}
              </div>
              <button type="button" data-testid="device-back-link" onClick={() => setDeviceLimit(null)}
                className="w-full text-center text-sm text-white/50 hover:text-[#007BFF] transition-colors">{t("back_to_login")}</button>
            </div>
          ) : tab === "register" ? (
            <form onSubmit={handleRegister} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="reg-firstname" className={inputCls} placeholder={t("firstname")} value={form.first_name} onChange={set("first_name")} required />
                <Input data-testid="reg-lastname" className={inputCls} placeholder={t("lastname")} value={form.last_name} onChange={set("last_name")} required />
              </div>
              <Input data-testid="reg-email" type="email" className={inputCls} placeholder={t("email")} value={form.email} onChange={set("email")} required />
              <div className="relative">
                <Input data-testid="reg-password" type={showPw ? "text" : "password"} className={`${inputCls} pr-10`} placeholder={t("password")} value={form.password} onChange={set("password")} required minLength={6} />
                <button type="button" data-testid="toggle-reg-password" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <Input data-testid="reg-company" className={inputCls} placeholder={t("company")} value={form.company_name} onChange={set("company_name")} />

              <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold text-base transition-all hover:scale-[1.02] gt-glow-blue">
                {loading ? <Loader2 className="animate-spin" /> : `${t("hero_cta")} ${t("hero_cta_sub")}`}
              </Button>
              <p className="text-center text-xs text-white/40 flex items-center justify-center gap-1">
                <Lock size={12} /> {t("secure_pay")}
              </p>
            </form>
          ) : tab === "forgot" ? (
            forgotSent ? (
              <div className="space-y-4 py-2 text-center" data-testid="forgot-sent-msg">
                <p className="text-sm text-white/70 leading-relaxed">{t("forgot_sent")}</p>
                <button type="button" data-testid="forgot-back-link" onClick={() => { setTab("login"); setForgotSent(false); }}
                  className="text-sm text-[#007BFF] hover:text-[#00E676] transition-colors">{t("back_to_login")}</button>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-3">
                <h3 className="font-display text-lg font-bold">{t("forgot_title")}</h3>
                <p className="text-xs text-white/40 -mt-1">{t("forgot_desc")}</p>
                <Input data-testid="forgot-email" type="email" className={inputCls} placeholder={t("email")} value={form.email} onChange={set("email")} required />
                <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold text-base transition-all hover:scale-[1.02]">
                  {loading ? <Loader2 className="animate-spin" /> : t("forgot_submit")}
                </Button>
                <button type="button" data-testid="forgot-back-link" onClick={() => setTab("login")}
                  className="w-full text-center text-sm text-white/50 hover:text-[#007BFF] transition-colors mt-1">{t("back_to_login")}</button>
              </form>
            )
          ) : (
            <form onSubmit={handleLogin} className="space-y-3">
              <Input data-testid="login-email" type="email" className={inputCls} placeholder={t("email")} value={form.email} onChange={set("email")} required />
              <div className="relative">
                <Input data-testid="login-password" type={showPw ? "text" : "password"} className={`${inputCls} pr-10`} placeholder={t("password")} value={form.password} onChange={set("password")} required />
                <button type="button" data-testid="toggle-login-password" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-12 font-semibold text-base transition-all hover:scale-[1.02]">
                {loading ? <Loader2 className="animate-spin" /> : t("login_submit")}
              </Button>
              <button type="button" data-testid="forgot-pw-link" onClick={() => { setTab("forgot"); setForgotSent(false); }}
                className="w-full text-center text-sm text-white/50 hover:text-[#007BFF] transition-colors mt-1">{t("forgot_pw")}</button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
